/**
 * The two ends of the history archive: what goes into one, and what comes out.
 *
 * `historyArchive.mjs` is the format — magic, KDF, chunks, nonces. This is the
 * part that knows where a conversation lives on this device and what has to be
 * fetched before it can be written down: the archive carries attachment BYTES,
 * and the only place to get them is the server, while its copy still exists.
 *
 * That ordering is the whole point. After a phone change the ciphertext on the
 * server cannot be opened by the new device, and files expire on their own
 * schedule. An export made today is the only thing that will still open in six
 * months, so it has to be complete today.
 */

import { packArchive, unpackArchive, buildPayload, readPayload, DEFAULT_ITERATIONS } from './historyArchive.mjs';
import { mergeMessages } from './messageStore.mjs';

const b64 = (bytes) => {
  let s = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    s += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(s);
};
const unb64 = (str) => {
  const bin = atob(str);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

/** File ids referenced by any message in these conversations. */
export function attachmentIds(conversations) {
  const ids = new Set();
  for (const conv of Object.values(conversations || {})) {
    for (const m of conv || []) if (m && m.file_id != null) ids.add(String(m.file_id));
  }
  return [...ids];
}

/**
 * Same walk, but carrying the envelope key alongside each id: a sealed
 * attachment (SEC-04) is ciphertext on the server, and the export must
 * decrypt it BEFORE packing — the archive's protection is the passphrase,
 * and a blob nobody can ever open again is not an archive.
 */
export function attachmentRefs(conversations) {
  const refs = new Map();
  for (const conv of Object.values(conversations || {})) {
    for (const m of conv || []) {
      if (!m || m.file_id == null) continue;
      const id = String(m.file_id);
      if (!refs.has(id) || (!refs.get(id).key && m.file_key)) {
        refs.set(id, { id, key: m.file_key || null });
      }
    }
  }
  return [...refs.values()];
}

/**
 * Everything this device holds, ready to be packed.
 *
 * `fetchAttachment` returns bytes for a file id, or null when the server no
 * longer has it. A missing file is reported rather than skipped silently: the
 * person is about to rely on this archive, and "3 of 40 pictures could not be
 * saved" is something they can act on today.
 */
export async function collectExport({ myUserId, archive, fetchAttachment, onProgress = null }) {
  const convIds = await archive.conversationIds();
  const conversations = {};
  for (const id of convIds) {
    const messages = await archive.getMessages(id);
    if (messages.length) conversations[id] = messages;
  }

  const refs = attachmentRefs(conversations);
  const attachments = [];
  const missing = [];
  let done = 0;
  for (const ref of refs) {
    try {
      const bytes = await fetchAttachment(ref.id, ref.key);
      if (bytes && bytes.length) attachments.push({ id: ref.id, b64: b64(bytes) });
      else missing.push(ref.id);
    } catch (e) {
      missing.push(ref.id);
    }
    done += 1;
    if (onProgress) onProgress(done, refs.length);
  }

  const messageCount = Object.values(conversations).reduce((n, c) => n + c.length, 0);
  return { conversations, attachments, missing, messageCount, conversationCount: Object.keys(conversations).length };
}

/** Pack what collectExport gathered. Returns the bytes to hand to the person. */
export async function makeArchive({ passphrase, myUserId, collected, iterations = DEFAULT_ITERATIONS,
                                    onProgress, masterSecret = null }) {
  const payload = buildPayload({
    myUserId,
    conversations: collected.conversations,
    attachments: collected.attachments,
    masterSecret,
  });
  return packArchive(passphrase, payload, { iterations, onProgress });
}

/**
 * Read an archive back into this device.
 *
 * Merging, not replacing: importing onto a phone that already has messages
 * must not erase them, and mergeMessages already knows how to prefer a copy
 * that opened over one that did not.
 */
export async function restoreArchive({ passphrase, bytes, archive, onProgress, adoptMaster = null }) {
  const payloadBytes = await unpackArchive(passphrase, bytes, { onProgress });
  const payload = readPayload(payloadBytes);

  // The account key, before the messages: a restore that filled the history
  // and then failed would otherwise leave a device that can read everything
  // and authorise nothing, which is the confusing half of both states.
  let master = null;
  if (payload.masterSecret && adoptMaster) {
    master = await adoptMaster(payload.masterSecret, payload.myUserId);
  }

  let restoredMessages = 0;
  for (const [convId, messages] of Object.entries(payload.conversations || {})) {
    const existing = await archive.getMessages(convId);
    await archive.replaceMessages(convId, mergeMessages(existing, messages));
    restoredMessages += messages.length;
  }

  let restoredAttachments = 0;
  for (const a of payload.attachments || []) {
    if (!a || !a.id || !a.b64) continue;
    await archive.putAttachment(a.id, a.b64);
    restoredAttachments += 1;
  }

  return {
    master,
    exportedAt: payload.exportedAt,
    fromUserId: payload.myUserId,
    conversations: Object.keys(payload.conversations || {}).length,
    restoredMessages,
    restoredAttachments,
  };
}

export { b64 as bytesToBase64, unb64 as base64ToBytes };
